import { ELink } from "../components/Editable";
import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { FadeUp, MaskedLine } from "../components/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { SEO } from "../components/SEO";
import RichText from "../components/RichText";
import { stripHtml } from "../lib/richtext";

export default function Faq() {
  const { t, ts, lang } = useLang();
  // Entrées ajoutées via l'assistant IA (questions fréquentes des visiteurs) —
  // fusionnées aux items fixes et incluses au schema FAQPage (résultats enrichis)
  const [extra, setExtra] = useState([]);
  useEffect(() => {
    api.get("/faq-extra", { params: { lang } })
      .then((r) => setExtra(r.data.items || []))
      .catch(() => {});
  }, [lang]);
  const items = [...t("faq.items"), ...extra];
  // Schéma FAQPage : les Q/R peuvent apparaître directement dans Google et les réponses IA
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map((i) => ({
      "@type": "Question",
      "name": stripHtml(i.q),
      "acceptedAnswer": { "@type": "Answer", "text": stripHtml(i.a) },
    })),
  };

  return (
    <div data-testid="faq-page" className="pt-32 pb-28">
      <SEO title={ts("seo.faq_title")} description={ts("seo.faq_desc")} path="/faq" jsonLd={faqLd} />
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <FadeUp>
          <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("faq.kicker")}</p>
          <h1 className="font-serif text-5xl md:text-6xl leading-tight mb-14">
            <MaskedLine key={ts("faq.title_1")} delay={0.15}>{t("faq.title_1")}</MaskedLine>
            <MaskedLine key={ts("faq.title_2")} delay={0.35} className="italic text-gold">{t("faq.title_2")}</MaskedLine>
          </h1>
        </FadeUp>

        <FadeUp delay={0.1}>
          <Accordion type="single" collapsible className="space-y-4">
            {items.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="bg-surface border border-gold/15 rounded-xl px-6 overflow-hidden"
              >
                <AccordionTrigger className="font-serif text-xl text-left py-6 hover:text-gold hover:no-underline transition-colors duration-300">
                  <RichText text={item.q} path={`faq.items.${i}.q`} />
                </AccordionTrigger>
                <AccordionContent className="text-ivory/60 leading-relaxed pb-6">
                  <RichText text={item.a} path={`faq.items.${i}.a`} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeUp>

        <FadeUp delay={0.15} className="text-center mt-16">
          <p className="text-ivory/60 mb-5">{t("faq.cta")}</p>
          <ELink
            editPath="link.faq_cta"
            to="/contact" data-testid="faq-contact-cta"
            className="group inline-flex items-center gap-3 border border-gold/40 text-gold px-8 py-3.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300"
          >
            {t("faq.cta_btn")}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </ELink>
        </FadeUp>
      </div>
    </div>
  );
}
